import { Timestamp, serverTimestamp, type FieldValue } from 'firebase/firestore'
import { z } from 'zod'

/**
 * Zod transformer that accepts Firestore Timestamp, ISO string, or Date and
 * always returns a JS Date. Use for every timestamp field read from Firestore.
 */
export const timestampToDate = z.preprocess((value) => {
  if (value == null) return value
  if (value instanceof Date) return value
  if (value instanceof Timestamp) return value.toDate()
  if (typeof value === 'object' && value !== null && 'seconds' in value && 'nanoseconds' in value) {
    const ts = value as { seconds: number; nanoseconds: number }
    return new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6))
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value)
  return value
}, z.date())

/** Same as `timestampToDate` but allows null/undefined → null. */
export const timestampToDateNullable = z
  .preprocess((value) => (value == null ? null : value), z.union([z.null(), timestampToDate]))
  .transform((v) => v ?? null)

/**
 * Server-stamped timestamp (e.g. `createdAt`/`updatedAt` written via
 * `serverTimestamp()`). With `persistentLocalCache`, `setDoc` echoes the doc
 * to the local listener BEFORE the server roundtrip — so server-stamped
 * fields arrive as `null` on the first read. Substitute `new Date()` to
 * represent the pending state; the server ack overwrites within ~1–2 s.
 *
 * Use only for fields written via `serverTimestamp()`. Fields that are
 * legitimately optional (e.g. `cancelledAt`) should use `timestampToDateNullable`.
 */
export const serverStampDate = z.preprocess((value) => {
  if (value == null) return new Date()
  if (value instanceof Date) return value
  if (value instanceof Timestamp) return value.toDate()
  if (typeof value === 'object' && value !== null && 'seconds' in value && 'nanoseconds' in value) {
    const ts = value as { seconds: number; nanoseconds: number }
    return new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6))
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value)
  return value
}, z.date())

/** Convert a JS Date back to a Firestore Timestamp for writes. */
export function dateToTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date)
}

/** Server timestamp marker for createdAt / updatedAt fields on writes. */
export function nowServerTimestamp(): FieldValue {
  return serverTimestamp()
}

/**
 * Strip `id` from a partial-update payload and convert any Date values to Timestamps.
 * Use before passing to `setDoc` / `updateDoc`.
 */
export function toFirestoreData<T extends Record<string, unknown>>(payload: T): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(payload)) {
    if (key === 'id') continue
    if (value === undefined) continue
    if (value instanceof Date) {
      result[key] = Timestamp.fromDate(value)
    } else {
      result[key] = value
    }
  }
  return result
}
